import React, { Component, Fragment } from 'react';
import { FulfillmentNote } from '../../types/schema';
import styled from 'styled-components';
import { Col, Row } from 'antd';
import { getStatusColor } from 'components/OrderTable/StatusIcon';
import { FormattedMessage } from 'react-intl';
import messages from 'containers/OrdersPage/messages';
import utilsMessages from 'utils/messages';
import moment from 'moment';
import { Link } from 'react-router-dom';
import Money from 'components/Money/Money';
import VatTooltip from 'components/VatTooltip';

interface FulfillmentNoteSummaryProps {
  fulfillmentNote: FulfillmentNote;
  isMobileSize: boolean;
}

export const Container = styled.div`
  .ant-col {
    width: 100% !important;
  }

  @media (max-width: 767px) {
    padding: 0;
  }
`;
const FulfillmentNoteRow = styled(Row)`
  margin-left: 0 !important;
  margin-right: 0 !important;
`;
const FulfillmentNoteCol = styled(Col)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;
export const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  &:not(:last-child) {
    margin-bottom: 12px;

    @media (max-width: 767px) {
      margin-bottom: 0;
    }
  }

  @media (max-width: 767px) {
    padding: 8px 12px;
    word-break: break-word;
  }
`;
const InfoRowHeader = styled(InfoRow)`
  padding: 16px;
  border-bottom: 1px solid #eaeaeb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const FulfillmentNoteId = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #343538;
`;
const FulfillmentNoteStatus = styled.p<{ status: string; isMobileSize?: boolean }>`
  font-size: ${({ isMobileSize }) => (isMobileSize ? '14px' : '16px')};
  font-weight: 500;
  line-height: ${({ isMobileSize }) => (isMobileSize ? '20px' : '24px')};
  color: ${({ status }) => getStatusColor(status)};
`;
const Label = styled.span<{ isMobileSize?: boolean }>`
  font-size: ${({ isMobileSize }) => (isMobileSize ? '12px' : '14px')};
  font-weight: 500;
  line-height: ${({ isMobileSize }) => (isMobileSize ? '16px' : '20px')};
  color: #808289;
  min-width: 180px;

  @media (max-width: 767px) {
    min-width: 180px;
  }
`;
const Value = styled(Label)`
  color: #343538;
  flex: 1;
  word-break: break-word;

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;
const OrderIdLinkValue = styled(Value)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const FulfillmentNoteColRightWrapper = styled.div`
  padding: 16px;
  @media (max-width: 767px) {
    padding: 12px;
  }
`;
const FulfillmentNoteColLeftWrapper = styled.div`
  padding: 16px;
  @media (max-width: 767px) {
    padding: 0 !important;
  }
`;

export const MoneyRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;
export const MoneyValue = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #343538;
`;
export const MoneyLabel = styled.span`
  color: #808289;
`;
const TotalLabel = styled(MoneyLabel)<{ isMobileSize?: boolean }>`
  color: #585a60;
  line-height: 24px;
  font-size: ${({ isMobileSize }) => (isMobileSize ? '16px' : '14px')};
  font-weight: 500;
`;
const FFMNoteInfo = styled.div<{ isMobileSize?: boolean }>`
  display: ${({ isMobileSize }) => (isMobileSize ? 'none' : 'flex')};
  align-items: center;
  padding: 12px 16px;
  justify-content: space-between;
`;
const FFMNoteInfoText = styled.p<{ isMobileSize?: boolean }>`
  font-size: ${({ isMobileSize }) => (isMobileSize ? '12px' : '14px')};
  line-height: ${({ isMobileSize }) => (isMobileSize ? '16px' : '20px')};
  color: #343538;
  font-weight: 400;
`;
const TotalMoney = styled.p`
  color: #343538;
  font-size: 18px;
  font-weight: 500;
`;
const FFMNoteInfoPrice = styled.div<{ isMobileSize?: boolean }>`
  flex-direction: column;
  align-items: flex-end;
  display: ${({ isMobileSize }) => (isMobileSize ? 'none' : 'flex')};
  gap: 4px;
`;
class FulfillmentNoteSummary extends Component<FulfillmentNoteSummaryProps> {
  renderMoneySection = (isMobileSize: boolean, fulfillmentNote: FulfillmentNote) => {
    return (
      <FulfillmentNoteColRightWrapper>
        <MoneyRow>
          <MoneyLabel>
            <FormattedMessage {...messages.subTotal} />
          </MoneyLabel>
          <TotalLabel isMobileSize={isMobileSize}>
            <Money type="new">{fulfillmentNote?.subTotalBeforeVat || 0}</Money>
          </TotalLabel>
        </MoneyRow>

        <MoneyRow>
          <MoneyLabel>
            <FormattedMessage {...utilsMessages.serviceFee} />
          </MoneyLabel>
          <TotalLabel isMobileSize={isMobileSize}>
            <Money type="new">{fulfillmentNote?.deliveryFee?.fee || 0}</Money>
          </TotalLabel>
        </MoneyRow>

        <MoneyRow>
          <MoneyLabel>
            <FormattedMessage {...utilsMessages.freeShippingText} />
          </MoneyLabel>
          <TotalLabel isMobileSize={isMobileSize}>
            {Number(fulfillmentNote?.deliveryFee?.discount) > 0 ? '-' : ''}
            <Money type="new">{fulfillmentNote?.deliveryFee?.discount || 0}</Money>
          </TotalLabel>
        </MoneyRow>

        <MoneyRow>
          <MoneyLabel>
            <FormattedMessage {...utilsMessages.promotion} />
          </MoneyLabel>
          <TotalLabel isMobileSize={isMobileSize}>
            {Number(fulfillmentNote?.discount) > 0 ? '-' : ''}
            <Money type="new">{fulfillmentNote?.discount || 0}</Money>
          </TotalLabel>
        </MoneyRow>

        <MoneyRow>
          <div style={{ display: 'flex', gap: '4px', color: 'rgba(0, 0, 0, 0.54)' }}>
            <MoneyLabel>VAT</MoneyLabel>
            <VatTooltip
              productVat={fulfillmentNote.totalVatOnProducts || 0}
              serviceFeeVat={
                !fulfillmentNote?.deliveryFee?.discount ? (fulfillmentNote?.deliveryFee?.additionalVat as number) : 0
              }
            />
          </div>
          <TotalLabel>
            <Money>{fulfillmentNote?.totalVat || 0}</Money>
          </TotalLabel>
        </MoneyRow>
      </FulfillmentNoteColRightWrapper>
    );
  };

  public render() {
    const { fulfillmentNote, isMobileSize } = this.props;

    return (
      <Container>
        <FulfillmentNoteRow gutter={8}>
          <FulfillmentNoteCol span={24}>
            {!isMobileSize ? (
              <InfoRowHeader>
                <FulfillmentNoteId>
                  <FormattedMessage {...messages.fulfillmentNote} />: {fulfillmentNote.fulfillmentNoteShortCode}
                </FulfillmentNoteId>
                <FulfillmentNoteStatus status={fulfillmentNote.firstOrder?.status}>
                  <FormattedMessage
                    id={`app.containers.OrdersPage.status.${fulfillmentNote.firstOrder?.status}`}
                    defaultMessage="{status}"
                    values={{ status: fulfillmentNote.firstOrder?.status }}
                  />
                </FulfillmentNoteStatus>
              </InfoRowHeader>
            ) : null}
          </FulfillmentNoteCol>
        </FulfillmentNoteRow>
        <FulfillmentNoteRow gutter={8} style={{ borderBottom: isMobileSize ? 'none' : '1px solid #EAEAEB' }}>
          <Fragment>
            <FulfillmentNoteCol lg={16} sm={24} style={{ borderRight: isMobileSize ? 'none' : '1px solid #EAEAEB' }}>
              {isMobileSize ? (
                <>
                  <InfoRow>
                    <Label isMobileSize={isMobileSize}>
                      <FormattedMessage {...utilsMessages.ffmNoteId} />
                    </Label>
                    <Value>{fulfillmentNote.fulfillmentNoteShortCode}</Value>
                  </InfoRow>

                  <InfoRow>
                    <Label isMobileSize={isMobileSize}>
                      <FormattedMessage {...utilsMessages.status} />
                    </Label>

                    <FulfillmentNoteStatus
                      status={fulfillmentNote.firstOrder?.status || ''}
                      isMobileSize={isMobileSize}
                    >
                      <FormattedMessage
                        id={`app.containers.OrdersPage.status.${fulfillmentNote.firstOrder?.status}`}
                        defaultMessage="{status}"
                        values={{ status: fulfillmentNote.firstOrder?.status }}
                      />
                    </FulfillmentNoteStatus>
                  </InfoRow>
                </>
              ) : null}

              <FulfillmentNoteColLeftWrapper>
                <InfoRow>
                  <Label isMobileSize={isMobileSize}>
                    <FormattedMessage {...utilsMessages.deliveryDate} />
                  </Label>
                  <Value>
                    {fulfillmentNote.deliveryStartDateTime && fulfillmentNote.deliveryEndDateTime
                      ? `${moment(fulfillmentNote?.deliveryStartDateTime).format('HH:mm')} - ${moment(fulfillmentNote?.deliveryEndDateTime).format('HH:mm')} | ${moment(fulfillmentNote?.deliveryStartDateTime).format('DD/MM/YYYY')}`
                      : '-'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <Label isMobileSize={isMobileSize}>
                    <FormattedMessage {...utilsMessages.paymentMethod} />
                  </Label>
                  <Value>
                    <FormattedMessage {...utilsMessages[fulfillmentNote?.paymentMethod || 'COD']} />
                  </Value>
                </InfoRow>
                <InfoRow>
                  <Label isMobileSize={isMobileSize}>
                    <FormattedMessage {...messages.orders} />
                  </Label>

                  <OrderIdLinkValue>
                    {fulfillmentNote?.orderIds?.map((item) => {
                      return (
                        <Link style={{ color: '#388BFF' }} key={item} to={`/orders/${item}`}>
                          {item}
                        </Link>
                      );
                    })}
                  </OrderIdLinkValue>
                </InfoRow>

                <InfoRow>
                  <Label isMobileSize={isMobileSize}/>
                  <Value>
                    (<FormattedMessage {...messages.combineText} />)
                  </Value>
                </InfoRow>
              </FulfillmentNoteColLeftWrapper>
            </FulfillmentNoteCol>

            {!isMobileSize && (
              <FulfillmentNoteCol lg={8} sm={24}>
                {this.renderMoneySection(isMobileSize, fulfillmentNote)}
              </FulfillmentNoteCol>
            )}
          </Fragment>
        </FulfillmentNoteRow>
        <FulfillmentNoteRow gutter={8}>
          <FulfillmentNoteCol lg={24} sm={24}>
            <FFMNoteInfo isMobileSize={isMobileSize}>
              <div>
                {/* <FFMNoteInfoText isMobileSize={isMobileSize}>
                  <FormattedMessage {...utilsMessages.deliveryPolicyMessage} />
                </FFMNoteInfoText>
                <FFMNoteInfoText isMobileSize={isMobileSize}>
                  <FormattedMessage
                    {...utilsMessages.shippingFeePolicyMessage}
                    values={{
                      thres: '400,000đ',
                      fee: '25,0000đ',
                    }}
                  />
                </FFMNoteInfoText> */}
              </div>

              <FFMNoteInfoPrice isMobileSize={isMobileSize}>
                <FFMNoteInfoText isMobileSize={isMobileSize}>
                  <FormattedMessage {...utilsMessages.total} />
                </FFMNoteInfoText>
                <TotalMoney>
                  <Money type="new">{fulfillmentNote?.total}</Money>
                </TotalMoney>
              </FFMNoteInfoPrice>
            </FFMNoteInfo>
          </FulfillmentNoteCol>
        </FulfillmentNoteRow>
      </Container>
    );
  }
}

export default FulfillmentNoteSummary;
